import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import Lottie from "react-lottie";
import styled from "styled-components";
import loginAnimation from "../Animations/login.json";
import { auth, db } from "../Config/config";

import avatar1 from "../Avatars/1.jpg";
import avatar2 from "../Avatars/2.jpg";
import avatar3 from "../Avatars/3.jpg";
import avatar4 from "../Avatars/4.jpg";

const colors = {
  primary: "#1a5f7a",
  secondary: "#57c5b6",
  accent: "#ffc93c",
  background: "#f9f9f9",
  text: "#333333",
  buttonPrimary: "#441B77",
  buttonHover: "#A52A2A",
};

const LoginRegistration = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState("1");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [error, setError] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const metaViewport = document.querySelector("meta[name=viewport]");
    if (metaViewport) {
      metaViewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
      );
    }
    return () => {
      if (metaViewport) {
        metaViewport.setAttribute(
          "content",
          "width=device-width, initial-scale=1.0"
        );
      }
    };
  }, []);

  const handleSuccessfulAuth = () => {
    // Get the saved redirect URL and package details from sessionStorage
    const redirectUrl = sessionStorage.getItem("redirectAfterLogin");
    const packageDetails = sessionStorage.getItem("packageDetails");

    // Clear the sessionStorage
    sessionStorage.removeItem("redirectAfterLogin");
    sessionStorage.removeItem("packageDetails");

    if (redirectUrl && redirectUrl.includes("/packagedetails/")) {
      // If we have package details and were viewing a package
      if (packageDetails) {
        navigate("/details", {
          state: {
            packageDetails: JSON.parse(packageDetails),
          },
          replace: true,
        });
      } else {
        // Just redirect to the package page if no details
        navigate(redirectUrl, { replace: true });
      }
    } else {
      // Default redirect to home
      navigate("/", { replace: true });
    }
  };

  const toggleScreen = () => {
    setIsLogin(!isLogin);
    setShowAdditionalFields(false);
    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      handleSuccessfulAuth();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!showAdditionalFields) {
      if (password !== confirmPassword) {
        setError("Passwords don't match");
        return;
      }
      setShowAdditionalFields(true);
    } else {
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;

        await updateProfile(user, { displayName: name });

        const signUpDate =
          new Date().toLocaleString("en-US", {
            timeZone: "Asia/Kolkata",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          }) + " UTC+5:30";

        await setDoc(doc(db, "users", user.uid), {
          avatarSelected: selectedAvatar,
          name,
          number,
          signUpDate,
        });

        handleSuccessfulAuth();
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const avatars = [avatar1, avatar2, avatar3, avatar4];

  const renderFormContent = () => {
    return (
      <>
        {!showAdditionalFields ? (
          <>
            <Title windowWidth={windowWidth}>
              {isLogin ? "Welcome Back!" : "Join Our Adventure"}
            </Title>
            <Form
              windowWidth={windowWidth}
              onSubmit={isLogin ? handleLogin : handleRegister}
            >
              <InputWrapper windowWidth={windowWidth}>
                <Input
                  windowWidth={windowWidth}
                  type="email"
                  placeholder="Email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </InputWrapper>
              <InputWrapper windowWidth={windowWidth}>
                <Input
                  windowWidth={windowWidth}
                  type="password"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </InputWrapper>
              {!isLogin && (
                <InputWrapper windowWidth={windowWidth}>
                  <Input
                    windowWidth={windowWidth}
                    type="password"
                    placeholder="Confirm Password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </InputWrapper>
              )}
              <ButtonWrapper windowWidth={windowWidth}>
                <Button windowWidth={windowWidth} type="submit">
                  {isLogin ? "Log In" : "Next"}
                </Button>
              </ButtonWrapper>
            </Form>
            <Link windowWidth={windowWidth} onClick={toggleScreen}>
              {isLogin
                ? "New to our adventures? Sign up"
                : "Already a traveler? Log in"}
            </Link>
          </>
        ) : (
          <>
            <Title windowWidth={windowWidth}>We are Happy to Join You!</Title>
            <AvatarContainer windowWidth={windowWidth}>
              {avatars.map((avatar, index) => (
                <Avatar
                  key={index}
                  src={avatar}
                  alt={`Avatar ${index + 1}`}
                  isSelected={selectedAvatar === (index + 1).toString()}
                  onClick={() => setSelectedAvatar((index + 1).toString())}
                  windowWidth={windowWidth}
                />
              ))}
            </AvatarContainer>
            <Form windowWidth={windowWidth} onSubmit={handleRegister}>
              <InputWrapper windowWidth={windowWidth}>
                <Input
                  windowWidth={windowWidth}
                  type="text"
                  placeholder="Full Name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </InputWrapper>
              <InputWrapper windowWidth={windowWidth}>
                <Input
                  windowWidth={windowWidth}
                  type="tel"
                  placeholder="Mobile Number"
                  required
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                />
              </InputWrapper>
              <ButtonWrapper windowWidth={windowWidth}>
                <Button windowWidth={windowWidth} type="submit">
                  Sign Up
                </Button>
              </ButtonWrapper>
            </Form>
            <Link windowWidth={windowWidth} onClick={toggleScreen}>
              Already a traveler? Log in
            </Link>
          </>
        )}
        {error && (
          <ErrorMessage windowWidth={windowWidth}>{error}</ErrorMessage>
        )}
      </>
    );
  };

  return (
    <Container>
      <InnerContainer windowWidth={windowWidth}>
        {windowWidth <= 768 ? (
          <>
            <Half windowWidth={windowWidth}>
              <AnimationContainer windowWidth={windowWidth}>
                <Lottie
                  options={{
                    loop: true,
                    autoplay: true,
                    animationData: loginAnimation,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                  height="100%"
                  width="100%"
                />
              </AnimationContainer>
            </Half>
            <FormContainer windowWidth={windowWidth}>
              {renderFormContent()}
            </FormContainer>
          </>
        ) : (
          <>
            <Half windowWidth={windowWidth}>
              <AnimationContainer windowWidth={windowWidth}>
                <Lottie
                  options={{
                    loop: true,
                    autoplay: true,
                    animationData: loginAnimation,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                  height="100%"
                  width="100%"
                />
              </AnimationContainer>
            </Half>
            <Half isScrollable={true} windowWidth={windowWidth}>
              {renderFormContent()}
            </Half>
          </>
        )}
      </InnerContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${colors.background};
  padding: 20px;
`;

const InnerContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: ${(props) => (props.windowWidth <= 768 ? "100%" : "900px")};
  height: ${(props) => (props.windowWidth <= 768 ? "auto" : "600px")};
  background-color: white;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  border-radius: ${(props) => (props.windowWidth <= 768 ? "20px" : "10px")};
  overflow: hidden;
  flex-direction: ${(props) => (props.windowWidth <= 768 ? "column" : "row")};
`;

const Half = styled.div`
  width: ${(props) => (props.windowWidth <= 768 ? "100%" : "50%")};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${(props) => (props.windowWidth <= 768 ? "20px" : "40px")};
  ${(props) =>
    props.isScrollable &&
    props.windowWidth <= 768 &&
    `
      max-height: calc(100vh - 220px);
      overflow-y: auto;
    `}
`;

const AnimationContainer = styled.div`
  width: 100%;
  height: ${(props) => (props.windowWidth <= 768 ? "200px" : "100%")};
  max-width: 400px;
  max-height: ${(props) => (props.windowWidth <= 768 ? "200px" : "400px")};
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: ${(props) => (props.windowWidth <= 768 ? "20px 0" : "0")};
`;

const Title = styled.h1`
  color: ${colors.primary};
  font-size: ${(props) => (props.windowWidth <= 768 ? "24px" : "28px")};
  font-weight: bold;
  text-align: center;
  margin-bottom: ${(props) => (props.windowWidth <= 768 ? "20px" : "30px")};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => (props.windowWidth <= 768 ? "15px" : "20px")};
  width: 100%;
  max-width: 400px;
`;

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${(props) => (props.windowWidth <= 768 ? "0 20px" : "0")};
  box-sizing: border-box;
`;

const Input = styled.input`
  padding: ${(props) => (props.windowWidth <= 768 ? "12px" : "15px")};
  font-size: 16px;
  border: 1px solid ${colors.secondary};
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
  &::placeholder {
    color: #bdbdbd;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${(props) => (props.windowWidth <= 768 ? "0 20px" : "0")};
  box-sizing: border-box;
`;

const Button = styled.button`
  padding: ${(props) => (props.windowWidth <= 768 ? "12px" : "15px")};
  font-size: ${(props) => (props.windowWidth <= 768 ? "16px" : "18px")};
  background-color: ${colors.buttonPrimary};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%;
  &:hover {
    background-color: ${colors.buttonHover};
  }
`;

const Link = styled.div`
  margin-top: ${(props) => (props.windowWidth <= 768 ? "15px" : "20px")};
  text-align: center;
  color: ${colors.primary};
  cursor: pointer;
  text-decoration: underline;
  font-size: ${(props) => (props.windowWidth <= 768 ? "13px" : "14px")};
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${(props) => (props.windowWidth <= 768 ? "15px" : "20px")};
  margin-bottom: ${(props) => (props.windowWidth <= 768 ? "15px" : "20px")};
`;

const Avatar = styled.img`
  width: ${(props) => (props.windowWidth <= 768 ? "50px" : "60px")};
  height: ${(props) => (props.windowWidth <= 768 ? "50px" : "60px")};
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid
    ${(props) => (props.isSelected ? colors.primary : "transparent")};
`;

const ErrorMessage = styled.div`
  color: red;
  text-align: center;
  margin-top: 10px;
  font-size: ${(props) => (props.windowWidth <= 768 ? "13px" : "14px")};
`;

export default LoginRegistration;
